import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { 
  LayoutDashboard, 
  FileText, 
  Download, 
  Smartphone, 
  Wifi, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  LogOut,
  Eye
} from 'lucide-react';

const ClientDashboard = () => {
  const { user, logout, isAuthenticated, isCliente } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || !isCliente) {
      navigate('/login-cliente');
    }
  }, [isAuthenticated, isCliente, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Header />
      
      <div className="flex-grow container mx-auto px-4 pt-48 pb-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 mb-8 text-white">
          <h1 className="text-3xl font-bold mb-2">Benvenuto, {user.nome} {user.cognome}!</h1>
          <p className="text-purple-100">Area Clienti - I tuoi servizi e contratti</p>
          <button
            onClick={handleLogout}
            className="mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors duration-200 flex items-center space-x-2"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>

        {/* Status Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">SIM Attive</h3>
              <Smartphone className="w-8 h-8 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Linee Fisse</h3>
              <Wifi className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Preventivi</h3>
              <FileText className="w-8 h-8 text-orange-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>
        </div>

        {/* Main Sections */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Preventivi Status */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-orange-400" />
              I Tuoi Preventivi
            </h2>
            <div className="space-y-3">
              <div className="p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">Nessun preventivo</h3>
                  <span className="text-xs bg-gray-600 px-2 py-1 rounded text-gray-300">
                    --
                  </span>
                </div>
                <p className="text-sm text-gray-400">
                  Non hai ancora richieste di preventivo attive.
                </p>
              </div>
            </div>
            <button className="mt-4 w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors duration-200">
              Richiedi Nuovo Preventivo
            </button>
          </div>

          {/* Services & Contracts */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2 text-green-400" />
              Servizi Attivi
            </h2>
            <div className="space-y-3">
              <div className="p-4 bg-gray-700 rounded-lg">
                <h3 className="font-semibold text-white mb-1">SIM Card Mobile</h3>
                <p className="text-sm text-gray-400">Nessuna SIM attiva</p>
                <div className="flex items-center mt-2 space-x-4">
                  <button className="text-xs text-blue-400 hover:text-blue-300 flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span>Dettagli</span>
                  </button>
                </div>
              </div>
              
              <div className="p-4 bg-gray-700 rounded-lg">
                <h3 className="font-semibold text-white mb-1">Internet Fisso</h3>
                <p className="text-sm text-gray-400">Nessuna linea fissa attiva</p>
                <div className="flex items-center mt-2 space-x-4">
                  <button className="text-xs text-blue-400 hover:text-blue-300 flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span>Dettagli</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Download className="w-6 h-6 mr-2 text-blue-400" />
            Documenti e Contratti
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Contratti</h3>
              <p className="text-sm text-gray-400 mb-3">Nessun contratto disponibile</p>
              <button className="text-sm bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-white transition-colors duration-200">
                Visualizza Tutti
              </button>
            </div>
            <div className="p-4 bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Fatture</h3>
              <p className="text-sm text-gray-400 mb-3">Nessuna fattura disponibile</p>
              <button className="text-sm bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-white transition-colors duration-200">
                Scarica Fatture
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Clock className="w-6 h-6 mr-2 text-purple-400" />
            Azioni Rapide
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <button className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
              <h3 className="font-semibold text-white mb-1">Richiedi Preventivo</h3>
              <p className="text-sm text-gray-400">Nuovo preventivo personalizzato</p>
            </button>
            <button className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
              <h3 className="font-semibold text-white mb-1">Contatta Supporto</h3>
              <p className="text-sm text-gray-400">Assistenza clienti</p>
            </button>
            <button className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
              <h3 className="font-semibold text-white mb-1">Aggiorna Profilo</h3>
              <p className="text-sm text-gray-400">Modifica informazioni account</p>
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-purple-500/10 border border-purple-500/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-purple-400 mb-2">🔐 Area Clienti Attiva!</h3>
          <p className="text-gray-300">
            Benvenuto nella tua area riservata. Qui potrai gestire i tuoi servizi, visualizzare contratti, 
            scaricare fatture e monitorare lo stato dei preventivi. Le funzionalità complete saranno 
            implementate gradualmente per offrirti la migliore esperienza possibile.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ClientDashboard;